import { datadogRum } from '@datadog/browser-rum'
import React, { useEffect, useMemo, useState } from 'react'
import { CircularProgress, Button, Typography } from '@mui/material'
import { useNavigate } from 'react-router'
import { useIdentity } from '@equipmentshare/auth0-react'
import { TextInput } from '../text-input/text-input'
import {
	appendJDLinkToHeapLocation,
	convertSnakeCaseToTitleCase,
	normalizePayloadString,
	trackAnalyticEvent,
	ANALYTICS_LOCATIONS,
	LANDING_ROUTE,
	sortRequiredFieldsLogically,
	queryClient,
	ACTIVE_FEEDS_QUERY_VARIABLES,
} from '../../utils'
import {
	useAvailableIntegrationQuery,
	useCreateFeedMutation,
	useSearchFeedsQuery,
	type RequiredFieldResult,
} from '../../graphql/generated/generated'
import { IframeLink } from '../iframe-link'
import { useModal, useToast } from '../../providers'
import { formatFeedInput, responseModalConfigs, isValidErrorType, removeExpiresAtFromRequiredFields } from '../../utils/integration-form'
import { getHelpUrl, T3_HELP_URL_KEY } from '../../utils/urls'
import { BackLink, FeedDescriptionSection, FooterButtonContainer, FooterContainer, FormInputSection, StyledForm } from './create-form.styles'
import { DEFAULT_AEMPV2_INTEGRATION_DESCRIPTION, DEFAULT_INTEGRATION_DESCRIPTION, getUniqueFeedDescription } from './utils'

type Form = {
	[key: string]: string | undefined,
}

type CreateFormProps = {
	id?: string,
	isFormDirty: boolean,
	setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>,
}

export const CreateForm = ( { id, isFormDirty, setIsFormDirty }: CreateFormProps ) => {
	const { companyName } = useIdentity()
	const { data, isLoading: isLoadingIntegration } = useAvailableIntegrationQuery( { id: String( id ) } )
	const { data: feedsData, isLoading: isLoadingFeeds } = useSearchFeedsQuery( ACTIVE_FEEDS_QUERY_VARIABLES )
	const { showModal, hideModal } = useModal()
	const { showToast } = useToast()
	const { mutateAsync: createFeedMutationAsync, isLoading: isSaving } = useCreateFeedMutation()
	const { isAEMPV2, requiredFields, helpUrls, name } = data?.integration ?? {}
	const navigate = useNavigate()
	const isLoading = isLoadingIntegration || isLoadingFeeds
	const existingFeedNames = useMemo( () => feedsData?.searchFeeds?.feeds?.map( ( feed ) => feed.name ) ?? [], [ feedsData ] )

	const [ formData, setFormData ] = useState<Form>( {} )
	const [ feedDescription, setFeedDescription ] = useState( '' )
	const [ feedDescriptionError, setFeedDescriptionError ] = useState( false )

	useEffect( () => {
		if ( !isLoading && !feedDescription ) {
			const defaultDescription = isAEMPV2 ? DEFAULT_AEMPV2_INTEGRATION_DESCRIPTION : DEFAULT_INTEGRATION_DESCRIPTION
			const uniqueDescription = getUniqueFeedDescription( defaultDescription, existingFeedNames )
			setFeedDescription( uniqueDescription )
			setFeedDescriptionError( false )
		}
	}, [ isLoading, isAEMPV2, existingFeedNames, feedDescription ] )

	const onFormChange = ( field: string, value: string ) => {
		setFormData( { ...formData, [ field ]: value } )
		setIsFormDirty( true )
	}

	const onFeedDescriptionChange = ( value: string ) => {
		setFeedDescription( value )
		setFeedDescriptionError( value.length > 32 )
		setIsFormDirty( true )
	}

	const renderField = ( field: RequiredFieldResult ) => {
		const { fieldName, description, protected: isProtected } = field
		const displayName = convertSnakeCaseToTitleCase( fieldName )

		return (
			<TextInput
				key={fieldName}
				name={fieldName}
				placeholder={''}
				label={displayName}
				description={description}
				isMasked={isProtected}
				onChange={ ( e ) => onFormChange( fieldName, e.target.value ) }
			/>
		)
	}

	const onSubmit = ( e: React.FormEvent<HTMLFormElement> ) => {
		e.preventDefault()

		const integration = data?.integration

		if ( !integration?.vendorId ) {
			showToast( { title: 'Error', message: 'Valid Integration is required', type: 'error' } )
			trackAnalyticEvent( ANALYTICS_LOCATIONS.CREATE_PAGE, 'Submission Error', 'Invalid Integration detected.' )
			return
		}

		const feedPayload = {
			input: {
				integrationKey: String( id ),
				name: normalizePayloadString( feedDescription ),
				vendorId: Number( integration.vendorId ),
				versionId: integration.versionId,
				verifyCredentials: true,
				...formatFeedInput( formData, integration.requiredFields ),
			},
		}

		createFeedMutationAsync( feedPayload ).then( ( response ) => {
			const result = response.createFeedMutation
			if ( result?.error ) {
				const errorType = result.error.type
				const modalConfig = isValidErrorType( errorType ) ? responseModalConfigs[ errorType ] : responseModalConfigs.ERROR
				showModal( modalConfig )
				trackAnalyticEvent( ANALYTICS_LOCATIONS.CREATE_PAGE, 'Submission Error', result.error.message || 'Unknown Error' )
			} else if ( result?.feedId && !result.error ) {
				queryClient.invalidateQueries( useSearchFeedsQuery.getKey( ACTIVE_FEEDS_QUERY_VARIABLES ) )
				showToast( { title: 'Integration Added', message: 'Your credentials have been accepted, and we\'ll now connect to the OEM.', type: 'success' } )
				navigate( LANDING_ROUTE )
				setIsFormDirty( false )

				trackAnalyticEvent(
					ANALYTICS_LOCATIONS.CREATE_PAGE,
					'Success',
					'Create Integration',
					{ name, ...( companyName && { companyName } ) },
				)
			}
		} ).catch( ( error ) => {
			showModal( responseModalConfigs.GENERIC_ERROR )
			datadogRum.addError( error as Error, { source: 'create-form', name: 'Submit Credentials Error', payload: feedPayload } )
			trackAnalyticEvent( ANALYTICS_LOCATIONS.CREATE_PAGE, 'Submission Error', error.message ?? 'Unknown Error' )
		} )

		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.CREATE_PAGE,
			'Click',
			'Submit Credentials',
		)
	}

	const onBackPress = () => {
		if ( isFormDirty ) {
			showModal( {
				title: 'Unsaved Changes',
				headerText: 'Integration has unsaved changes',
				content: 'Changes to this integration will be lost. Are you sure you want to leave?',
				buttons: [
					{ label: 'No, stay', variant: 'text', color: 'primary', onClick: () => hideModal() },
					{ label: 'Yes, leave',
						variant: 'contained',
						color: 'primary',
						onClick: () => {
							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.CREATE_PAGE,
								'Click',
								appendJDLinkToHeapLocation( 'Back Button', name ),
							)
							setIsFormDirty( false )
							navigate( LANDING_ROUTE )
						} },
				],
			} )
		} else {
			trackAnalyticEvent( ANALYTICS_LOCATIONS.CREATE_PAGE, 'Click', 'Back Button' )
			navigate( LANDING_ROUTE )
		}
	}

	// show unsaved changes error in browser
	useEffect( () => {
		const handleBeforeUnload = ( e: BeforeUnloadEvent ) => {
			if ( isFormDirty ) {
				e.preventDefault()
				e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
				return 'You have unsaved changes. Are you sure you want to leave?'
			} else {
				return undefined
			}
		}

		window.addEventListener( 'beforeunload', handleBeforeUnload )

		return () => {
			window.removeEventListener( 'beforeunload', handleBeforeUnload )
		}
	}, [ isFormDirty ] )

	const shouldDisableSubmit = feedDescriptionError || !feedDescription.trim() || requiredFields?.some( ( { fieldName, required } ) => required && !formData[ fieldName ] )

	if ( isLoading ) {
		return <CircularProgress />
	}
	const t3HelpUrl = getHelpUrl( T3_HELP_URL_KEY, helpUrls )

	const sortedRequiredFields = removeExpiresAtFromRequiredFields( sortRequiredFieldsLogically( requiredFields ?? [] ) )

	return (
		<StyledForm noValidate autoComplete="off" aria-label={`create-form-${name}`} name={`create-form-${id}`} onSubmit={onSubmit}>
			<FormInputSection spacing={2}>
				<Typography variant="h3" gutterBottom>
					Enter your credentials for {name}
				</Typography>
				{ sortedRequiredFields.map( ( field ) => renderField( field ) ) }
				<Typography variant="body2" color="primary">
					<IframeLink href={t3HelpUrl} target="_blank" onClick={() => trackAnalyticEvent( ANALYTICS_LOCATIONS.CREATE_PAGE, 'Click', 'Don\'t have credentials? Link' )}>
						Don&apos;t have credentials?
					</IframeLink>
				</Typography>
			</FormInputSection>
			<FormInputSection spacing={2}>
				<FeedDescriptionSection spacing={2}>
					<Typography variant="h3" gutterBottom>
						Edit Feed Description (Optional)
					</Typography>
					<TextInput
						name="feedDescription"
						label="Feed Description"
						placeholder="Enter a description for this feed"
						value={feedDescription}
						required
						onBlur={() => onFeedDescriptionChange( feedDescription.trim() )}
						onChange={ ( { target } ) => onFeedDescriptionChange( target.value ) }
						error={feedDescriptionError}
						errorText={feedDescriptionError ? 'Description must be 32 characters or less' : undefined}
					/>
				</FeedDescriptionSection>
			</FormInputSection>
			<FooterContainer>
				<FooterButtonContainer>
					<BackLink onClick={onBackPress}>
						<Typography variant="body1" color="primary">
						Back
						</Typography>
					</BackLink>
					<Button
						variant={shouldDisableSubmit ? 'outlined' : 'contained'}
						type="submit"
						disabled={shouldDisableSubmit}
						loading={isSaving}
					>
						<Typography variant="body1">
							Submit credentials
						</Typography>
					</Button>
				</FooterButtonContainer>
			</FooterContainer>
		</StyledForm>
	)
}
